import { P, match } from "ts-pattern";
import { flow, pipe } from "effect";
import { durationToSeconds } from "../date";
import {
  Amount,
  makeAmount,
  makePercent,
  Percent,
  PERCENT_DECIMALS,
  PERCENT_ONE_HUNDRED,
  PERCENT_ZERO,
  Usd,
} from "@silo/types";
import { convertToUsd } from "../amount";
import { shiftDecimal } from "../bigint";
import { Schema } from "@effect/schema";
import { satisfies } from "effect/Function";

export type UtilizationState = "optimal" | "aboveOptimal" | "critical";

export type AprType = "deposit" | "borrow";

export const AprState = Schema.Literal(
  "stable",
  "increasingSlowly",
  "increasingRapidly",
  "decreasing"
).annotations({ identifier: "AprState" });

export type AprState = typeof AprState.Type;
export const aprStates = AprState.literals;
export const isAprState = Schema.is(AprState);

export const DEFAULT_APR_STATE = satisfies<AprState>()("stable");

export function getCurrentUtilizationDescription(
  aprState: AprState | null,
  utilizationState: UtilizationState
): string {
  return match({ aprState, utilizationState })
    .with(
      { aprState: "decreasing" },
      () =>
        "The utilization recently returned to the optimal region from a point above, causing the APR to start decreasing. The APR will keep decreasing until it has reached a stable rate. "
    )
    .with(
      { utilizationState: "optimal" },
      () =>
        "The current utilization is in the optimal region and therefore the APR doesn't increase or decrease with time. "
    )
    .with(
      { utilizationState: "aboveOptimal" },
      () =>
        "The current utilization is outside the optimal region and therefore the APR will keep increasing until the utilization goes back to the optimal region as a result of users depositing liquidity or repaying loans."
    )
    .with(
      { utilizationState: "critical" },
      () =>
        "The current utilization is in the critical region and therefore the APR will keep increasing rapidly until the utilization goes back to the optimal region as a result of users depositing liquidity or repaying loans."
    )
    .exhaustive();
}

export function getNewUtilizationDescription(
  currentUtilizationState: UtilizationState,
  newUtilizationState?: UtilizationState
): string | undefined {
  return (
    match([currentUtilizationState, newUtilizationState])
      /**
       * When the utilization is increasing outside of the optimal region, but not to
       * the critical region.
       */
      .with(
        ["optimal", "aboveOptimal"],
        () =>
          "The current utilization will increase to a point outside the optimal region, causing the APR state to transition from stable to dynamic. The new dynamic APR will keep increasing slowly until the utilization goes back to the optimal region as a result of users depositing liquidity or repaying loans."
      )
      /**
       * When the utilization is increasing to the critical region.
       */
      .with(
        [P.union("optimal", "aboveOptimal"), "critical"],
        () =>
          "The current utilization will increase to a point in the critical region, causing the APR state to transition from stable to dynamic. The new dynamic APR will keep increasing rapidly until the utilization goes back to the optimal region as a result of users depositing liquidity or repaying loans."
      )
      /**
       * When the utilization is decreasing to the above optimal region.
       */
      .with(
        ["critical", "aboveOptimal"],
        () =>
          "The current utilization will decrease to a point between the optimal and critical region, causing the transition of APR state to slow growth. The new APR will keep increasing slowly until the utilization goes back to the optimal region as a result of users depositing liquidity or repaying loans."
      )
      .with(
        [P.union("aboveOptimal", "critical"), "optimal"],
        () =>
          "The current utilization will decrease from a point above to the optimal region, which will cause the APR to start to decrease. The APR will continue to decrease until it reaches a stable rate."
      )
      .otherwise(() => undefined)
  );
}

const durationToBigIntSeconds = flow(durationToSeconds, BigInt);
const SECONDS_IN_YEAR = durationToBigIntSeconds({ years: 1 });

interface CalculateRewardsAprParams {
  emissionPerSecond: Amount;
  rewardTokenPriceUsd: Usd;
  rewardTokenDecimals: number;
  totalTokens: Amount;
  tokenPriceUsd: Usd;
  tokenDecimals: number;
}

export function calculateRewardsApr({
  emissionPerSecond,
  rewardTokenDecimals,
  rewardTokenPriceUsd,
  totalTokens,
  tokenPriceUsd,
  tokenDecimals,
}: CalculateRewardsAprParams): Percent {
  // 1. extrapolate emissionPerSecond to a year to figure out emission per year
  const emissionPerYear = pipe(emissionPerSecond * SECONDS_IN_YEAR, makeAmount);

  const emissionPerYearUsd = convertToUsd(
    emissionPerYear,
    rewardTokenDecimals,
    rewardTokenPriceUsd
  );

  const totalTokensUsd = convertToUsd(
    totalTokens,
    tokenDecimals,
    tokenPriceUsd
  );

  if (totalTokensUsd === 0n) {
    return PERCENT_ZERO;
  }

  const apr =
    shiftDecimal(emissionPerYearUsd, PERCENT_DECIMALS) / totalTokensUsd;

  return makePercent(apr);
}

export function getCollateralBaseApr(
  protocolShareFee: Percent,
  rawCollateralBaseApr: Percent
): Percent {
  const userShare = PERCENT_ONE_HUNDRED - protocolShareFee;

  return pipe(
    shiftDecimal(userShare * rawCollateralBaseApr, -PERCENT_DECIMALS),
    makePercent
  );
}
